import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import Skeleton from '@material-ui/lab/Skeleton';
import { Menu as MuiMenu, MenuItem as MuiMenuItem } from '@material-ui/core';
import { Box, Button, Icon, makeStyles, Typography } from '@popmenu/common-ui';
import { ChevronDown } from '@popmenu/web-icons';
import { screenReaderOnly } from '../../../assets/jss/utils/screenReaderOnly';
import { useRootPlacesData } from '../../hooks/data/useRootPlacesData';
import { Meta } from '../Meta';

type DishFeedTitleProps = {
  isForYou?: boolean
  isHomePage: boolean
  placeSlug: string
}

const useStyles = makeStyles(theme => ({
  container: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.gray.light}`,
    display: 'flex',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  dropdownButton: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1rem',
      fontWeight: 'bold',
    },
    color: theme.palette.primary.main,
    fontSize: '2rem',
    lineHeight: 'normal',
    paddingBottom: 0,
    paddingTop: 0,
  },
  dropdownSkeleton: {
    [theme.breakpoints.down('sm')]: {
      height: '42px',
      width: theme.spacing(22),
    },
    [theme.breakpoints.down('xs')]: {
      height: '37px',
      width: theme.spacing(18),
    },
    height: '60px',
    marginLeft: theme.spacing(0.5),
    width: theme.spacing(24),
  },
  title: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
      marginRight: theme.spacing(0.5),
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1rem',
      fontWeight: 500,
      marginRight: theme.spacing(0.5),
    },
    fontSize: '2rem',
    lineHeight: 'normal',
    marginRight: theme.spacing(1),
  },
  titleHiddenName: {
    ...screenReaderOnly,
  },
}));

// The page heading with a place picker; on For You the picker stays on For You
export const DishFeedTitle = ({ isForYou = false, isHomePage, placeSlug }: DishFeedTitleProps) => {
  const classes = useStyles();
  const intl = useIntl();

  const { data: placesData } = useRootPlacesData();
  const places = placesData?.rootPlaces?.records ?? [];
  const selectedPlace = places.find(place => place.slug === placeSlug);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const onButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const onClose = () => {
    setAnchorEl(null);
  };
  const muiMenuId = 'places-menu';

  let documentTitle: string | undefined;
  if (isHomePage) {
    documentTitle = 'Discover Local Trending Dishes';
  } else if (isForYou && selectedPlace) {
    documentTitle = selectedPlace.slug === 'global' ?
      intl.formatMessage({ defaultMessage: 'Dishes for You' }) :
      intl.formatMessage({ defaultMessage: 'Dishes for You in {placeName}' }, { placeName: selectedPlace.name });
  } else {
    documentTitle = selectedPlace?.feedTitle;
  }

  return (
    <Box className={classes.container}>
      {selectedPlace && (
        // For You is personal, so its canonical is the place's public feed
        <Meta canonicalPath={isHomePage ? '/' : `/places/${selectedPlace.slug}`} title={documentTitle} />
      )}
      <Typography className={classes.title} component="h1" variant="h1">
        <span>
          {isForYou ?
            <FormattedMessage defaultMessage="Dishes for you near" /> :
            <FormattedMessage defaultMessage="Trending dishes near" />}
        </span>
        {selectedPlace && (
          <span className={classes.titleHiddenName}>
            {selectedPlace.name}
          </span>
        )}
      </Typography>
      {!selectedPlace && (
        <Skeleton className={classes.dropdownSkeleton} />
      )}
      {selectedPlace && (
        <Box>
          <Button
            aria-haspopup="true"
            aria-owns={anchorEl ? muiMenuId : undefined}
            className={classes.dropdownButton}
            endIcon={<Icon icon={ChevronDown} />}
            onClick={onButtonClick}
            variant="text"
          >
            {selectedPlace.name}
          </Button>
          <MuiMenu
            anchorEl={anchorEl}
            id={muiMenuId}
            open={Boolean(anchorEl)}
            onClose={onClose}
          >
            {places.map(place => (
              <MuiMenuItem
                key={place.slug}
                component={Link}
                onClick={onClose}
                to={isForYou ? `/places/${place.slug}/for-you` : `/places/${place.slug}`}
              >
                {place.name}
              </MuiMenuItem>
            ))}
          </MuiMenu>
        </Box>
      )}
    </Box>
  );
};
